/**
 * @file Shared React component — components/common/ConfirmModal.tsx.
 */

import React from "react";
import "./ConfirmModal.css";
import Button from "./Button";
import OutlineButton from "./OutlineButton";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  confirmButtonVariant?: "primary" | "danger";
  children?: React.ReactNode;
  footerStart?: React.ReactNode;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  description,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  confirmButtonVariant = "primary",
  children,
  footerStart,
}) => {
  if (!open) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-card fade-in">
        {/* HEADER */}
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onCancel} disabled={loading}>
            ×
          </button>
        </div>

        {/* CONTENT */}
        <div className="modal-body">
          <p>{description}</p>
          {children}
        </div>

        {/* FOOTER */}
        <div className={`modal-footer${footerStart ? " modal-footer--split" : ""}`}>
          {footerStart ? <div className="modal-footer__start">{footerStart}</div> : null}
          <div className="modal-footer__actions">
            <OutlineButton
              label={cancelText}
              onClick={onCancel}
              disabled={loading}
              type="button"
            />
            <Button
              label={loading ? "Processing..." : confirmText}
              onClick={onConfirm}
              disabled={loading}
              type="button"
              variant={confirmButtonVariant}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/** React component — Confirm Modal. */
export default ConfirmModal;

